import {Massronome, Ticket} from '@naver/massronome-v2-sdk'

import {qs} from '@shared/helpers/queryString'

import getApi, {createApiAsyncThunk, createWrapper, getBaseUrl} from '@web/helpers/api'
import {Sentry} from '@web/helpers/log'
import {sendErrorToNelo} from '@web/helpers/nelo'
import {orderInterrupted} from '@web/middlewares/orderInterrupted'

import {requestEnter, setExpel, setWaitingQueueInfo} from './actions'
import {envName, getMassronomeSseUrl, getPageName, getPathName} from './constants'
import {
    QueuePollingResponse,
    ThunkApi,
    TicketExpelEvent,
    TicketExpelPollingResponse,
    WaitQueueRefreshEvent,
} from './interfaces'

/**
 * @description 대기열 V2 polling 시 Queue 정보 조회
 * massronome 서버를 바로 호출하면 브라우저에 엔드포인트가 노출되어
 * 공격 받을 수 있으므로, BFF를 경유하여 호출
 */
export const getQueueInfoByPolling = createApiAsyncThunk<
    QueuePollingResponse,
    {namespace: string; queueId: string},
    ThunkApi
>('waiting/getQueueInfoByPolling', async ({namespace, queueId}, {getState}) => {
    const orderApi = getApi(getBaseUrl())
    const state = getState()
    const pageSet = state.common.pageSet

    try {
        const url = qs.stringifyUrl({
            url: `${getPathName(pageSet)}/waiting/v2/polling/queue`,
            query: {
                namespace,
                queueId,
            },
        })
        return createWrapper<QueuePollingResponse>(orderApi.get(url))
    } catch (e) {
        if (e instanceof Error) {
            const entry = state.common.entry
            sendErrorToNelo(entry ?? namespace, e, pageSet)
        }
        throw e
    }
})

/**
 * @description 대기열 V2 polling 시 추방 여부 조회
 * massronome 서버를 바로 호출하면 브라우저에 엔드포인트가 노출되어
 * 공격 받을 수 있으므로, BFF를 경유하여 호출
 */
export const getTicketExpelByPolling = createApiAsyncThunk<
    TicketExpelPollingResponse,
    {namespace: string; ticketId: string},
    ThunkApi
>('waiting/getTicketExpelByPolling', async ({namespace, ticketId}, {getState}) => {
    const orderApi = getApi(getBaseUrl())
    const state = getState()
    const pageSet = state.common.pageSet

    try {
        const url = qs.stringifyUrl({
            url: `${getPathName(pageSet)}/waiting/v2/polling/expel`,
            query: {
                namespace,
                ticketId,
            },
        })
        return createWrapper<TicketExpelPollingResponse>(orderApi.get(url))
    } catch (e) {
        if (e instanceof Error) {
            const entry = state.common.entry
            sendErrorToNelo(entry ?? namespace, e, pageSet)
        }
        throw e
    }
})

export const getQueueInfo = createApiAsyncThunk<null, Ticket, ThunkApi>(
    'waiting/getQueueInfo',
    async (ticket, {getState, dispatch}) => {
        const state = getState()
        const pageSet = state.common.pageSet
        const initNamespace = `order-sheet-${getPageName(pageSet)}-${envName}`
        const client = new Massronome(initNamespace, ticket, {
            sseUrl: getMassronomeSseUrl(),
            fallback: {
                fetchQueueStatus: (namespace: string, queueId: string) => {
                    return dispatch(getQueueInfoByPolling({namespace, queueId})).unwrap()
                },
                fetchTicketExpelStatus: (namespace: string, ticketId: string) => {
                    return dispatch(getTicketExpelByPolling({namespace, ticketId})).unwrap()
                },
            },
        })

        client.on('refresh', (refreshData: WaitQueueRefreshEvent) => {
            refreshData && dispatch(setWaitingQueueInfo(refreshData))
            Sentry.sendMessage(`${ticket.ticketId} 대기열`, {data: refreshData})
        })

        client.on('expel', (expelData: TicketExpelEvent) => {
            expelData && dispatch(setExpel(expelData))
            Sentry.sendMessage(`${expelData.namespaceId} / ${expelData.expelId} 추방`, {data: expelData})
        })

        client.on('enterable', () => {
            dispatch(requestEnter())
        })

        client.on('error', (error: Error) => {
            Sentry.sendMessage(`${ticket.ticketId} 대기열 SSE error event 수신`, {errorData: error})
            Sentry.sendGeneralError(error)
        })

        /**
         * 1. sse 연결 3회 시도 실패 시
         * polling 전환하여 fallback 실행 (fetchQueueStatus, fetchTicketExpelStatus)
         * 2. polling 요청 5회 실패 시
         * sdk 에서 fatal 이벤트 발생
         * 3. fatal 이벤트 수신 시 주문서 페이지에서 강제로 내보냄
         */
        client.on('fatal', (error: Error) => {
            dispatch(
                orderInterrupted([
                    {type: 'ALERT', message: '대기열 정보를 확인할 수 없습니다.'},
                    {type: 'BACK', shouldClose: true},
                ]),
            )
            Sentry.sendMessage(`${ticket.ticketId} 대기열 SSE fatal event 수신`, {errorData: error})
            Sentry.sendGeneralError(error)
        })

        client.connect()

        return null
    },
)
